// ── Service de Storage (núcleo/aplicação) ─────────────────────────
// Regras/validações do domínio (tipo, tamanho, path, cache-bust). Recebe o
// repositório via porta (DI) — não conhece Supabase. Wiring em src/app/storage.ts.

import type { IStorageRepository } from '../ports/IStorageRepository';

export const MAX_AVATAR_BYTES = 2 * 1024 * 1024; // 2 MB
export const EMPRESA_LOGO_BUCKET = 'empresa-logos';

const extFromFile = (file: File): string => {
  const fromName = (file?.name || '').split('.').pop();
  if (fromName && fromName.length <= 5) return fromName.toLowerCase();
  const fromType = (file?.type || '').split('/').pop();
  return (fromType || 'jpg').toLowerCase();
};

export class StorageService {
  constructor(private readonly repo: IStorageRepository) {}

  /**
   * Faz upload da foto de perfil e retorna a URL pública (com cache-bust).
   */
  uploadAvatar = async (file: File, userId: string): Promise<string> => {
    if (!file) throw new Error('Selecione um arquivo.');
    if (!userId) throw new Error('Usuário inválido.');
    if (!/^image\//.test(file.type || '')) {
      throw new Error('Envie um arquivo de imagem (JPG, PNG, etc).');
    }
    if (file.size > MAX_AVATAR_BYTES) {
      throw new Error('Imagem muito grande. Máximo 2 MB.');
    }

    const path = `${userId}/avatar.${extFromFile(file)}`;
    const publicUrl = await this.repo.uploadAvatar({
      path,
      file,
      contentType: file.type || 'image/jpeg',
    });
    if (!publicUrl) throw new Error('Não foi possível obter a URL da imagem.');

    // cache-bust: força o navegador a recarregar após sobrescrever o mesmo path
    return `${publicUrl}?v=${Date.now()}`;
  };

  /**
   * Persiste a URL do avatar no perfil do usuário.
   */
  setProfileAvatar = async (userId: string, url: string): Promise<string> => {
    await this.repo.setProfileAvatar(userId, url);
    return url;
  };

  /**
   * Sobe a logo da empresa e devolve a URL pública (com cache-bust).
   *
   * O path é `<workspaceId>/<empresaId>.<ext>` — a pasta raiz é o
   * workspace, não o usuário como no avatar, porque a policy do bucket
   * libera qualquer membro ativo daquele workspace. Amarrar ao usuário
   * faria a segunda pessoa a editar escrever noutro path e a logo antiga
   * ficaria órfã no bucket.
   */
  uploadEmpresaLogo = async (file: File, workspaceId: string, empresaId: string): Promise<string> => {
    if (!file) throw new Error('Selecione um arquivo.');
    if (!workspaceId) throw new Error('Workspace inválido.');
    if (!empresaId) throw new Error('Empresa inválida.');
    if (!/^image\//.test(file.type || '')) {
      throw new Error('Envie um arquivo de imagem (JPG, PNG, etc).');
    }
    if (file.size > MAX_AVATAR_BYTES) {
      throw new Error('Imagem muito grande. Máximo 2 MB.');
    }

    const path = `${workspaceId}/${empresaId}.${extFromFile(file)}`;
    const publicUrl = await this.repo.uploadImage({
      bucket: EMPRESA_LOGO_BUCKET,
      path,
      file,
      contentType: file.type || 'image/jpeg',
    });
    if (!publicUrl) throw new Error('Não foi possível obter a URL da imagem.');

    // cache-bust: o path é o mesmo a cada troca (upsert), então sem isto
    // o navegador continuaria mostrando a logo anterior.
    return `${publicUrl}?v=${Date.now()}`;
  };

  /**
   * Persiste a URL da logo na empresa. `null` remove a logo.
   */
  setEmpresaLogo = async (empresaId: string, url: string | null): Promise<string | null> => {
    if (!empresaId) throw new Error('Empresa inválida.');
    await this.repo.setEmpresaLogo(empresaId, url);
    return url;
  };
}
